import type { Locale, PageId, PageType } from "./types"
import { LOCALES } from "./types"
import { SITE_URL } from "./site"
import { services } from "./services"
import { locations } from "./locations"
import { cases } from "./cases"
import { knowledgeArticles } from "./knowledge/index"

/**
 * The page registry is the single source of truth for every indexable URL.
 *
 * It feeds:
 *   1. React Router prerender()          (react-router.config.ts)
 *   2. Sitemap generation                (scripts/postbuild.mjs via scripts/generate-sitemap.mts)
 *   3. SEO metadata & hreflang           (src/seo/meta.ts)
 *   4. Language switching                (src/components/Header/Header.tsx)
 *   5. Breadcrumbs                       (src/components/Breadcrumbs.tsx)
 *   6. SEO validation                    (scripts/seo/*)
 *
 * Never hand-maintain route lists, sitemaps or prerender paths separately.
 */

export interface RegistryEntry {
  id: PageId
  pageType: PageType
  /** Absolute site paths per locale, always with trailing slash, e.g. "/nl/diensten/". */
  path: Partial<Record<Locale, string>>
  title: Record<Locale, string>
  description: Record<Locale, string>
  h1?: Record<Locale, string>
  /** Breadcrumb parent page id (undefined = top level under Home). */
  parentId?: PageId
  indexable: boolean
  lastModified?: string
}

const LAST_MODIFIED = "2026-10-29"

function localizedPath(locale: Locale, segments: string): string {
  const normalized = segments.replace(/^\//, "").replace(/\/$/, "")
  return normalized ? `/${locale}/${normalized}/` : `/${locale}/`
}

// ---------------------------------------------------------------------------
// Static pages
// ---------------------------------------------------------------------------

const staticPages: RegistryEntry[] = [
  {
    id: "home",
    pageType: "home",
    path: { nl: "/nl/", en: "/en/" },
    title: {
      nl: "AI Bedrijf Maastricht & Limburg | Schmeitz De Groen AI Labs",
      en: "Schmeitz De Groen AI Labs | Custom AI Development & Automation",
    },
    description: {
      nl: "AI bedrijf en developmentbureau vanuit Maastricht voor maatwerk AI-systemen, automatisering en agents. Gebouwd door Data Science & AI-alumni van de Universiteit Maastricht.",
      en: "AI development agency based in Maastricht building custom AI systems, automation and agents. Founded by Data Science & AI alumni of Maastricht University.",
    },
    indexable: true,
    lastModified: LAST_MODIFIED,
  },
  {
    id: "about",
    pageType: "about",
    path: { nl: "/nl/over-ons/", en: "/en/about/" },
    title: {
      nl: "Over Ons | Schmeitz De Groen AI Labs",
      en: "About Us | Schmeitz De Groen AI Labs",
    },
    description: {
      nl: "Ontmoet het compacte ingenieursteam achter Schmeitz De Groen AI Labs: Data Science & AI-alumni van de Universiteit Maastricht die sneller bouwen dan de grote spelers.",
      en: "Meet the small engineering team behind Schmeitz De Groen AI Labs: Data Science & AI alumni from Maastricht University who build faster than the giants.",
    },
    h1: { nl: "Over ons", en: "About Us" },
    indexable: true,
    lastModified: LAST_MODIFIED,
  },
  {
    id: "contact",
    pageType: "contact",
    path: { nl: "/nl/contact/", en: "/en/contact/" },
    title: {
      nl: "Contact | Schmeitz De Groen AI Labs",
      en: "Contact | Schmeitz De Groen AI Labs",
    },
    description: {
      nl: "Neem contact op met Schmeitz De Groen AI Labs over maatwerk AI, automatisering of workshops. We reageren doorgaans binnen 24 uur.",
      en: "Get in touch with Schmeitz De Groen AI Labs about custom AI, automation or workshops. We typically respond within 24 hours.",
    },
    h1: { nl: "Laten we praten", en: "Let's talk" },
    indexable: true,
    lastModified: LAST_MODIFIED,
  },
  {
    id: "privacy",
    pageType: "privacy",
    path: { nl: "/nl/privacy/", en: "/en/privacy/" },
    title: {
      nl: "Privacyverklaring | Schmeitz De Groen AI Labs",
      en: "Privacy Statement | Schmeitz De Groen AI Labs",
    },
    description: {
      nl: "De privacyverklaring van Schmeitz De Groen AI Labs: welke persoonsgegevens wij verwerken, waarom, hoe lang en welke rechten u heeft.",
      en: "The privacy statement of Schmeitz De Groen AI Labs: which personal data we process, why, for how long and what your rights are.",
    },
    h1: { nl: "Privacyverklaring", en: "Privacy Statement" },
    indexable: true,
    lastModified: LAST_MODIFIED,
  },
]

// ---------------------------------------------------------------------------
// Service pages
// ---------------------------------------------------------------------------

const SERVICE_INDEX_PARENT: PageId = "home"

const serviceIndex: RegistryEntry = {
  id: "services",
  pageType: "service-index",
  path: { nl: "/nl/diensten/", en: "/en/services/" },
  title: {
    nl: "Diensten | Maatwerk AI, Automatisering & Workshops",
    en: "Services | Custom AI, Automation & Workshops",
  },
  description: {
    nl: "Onze diensten: maatwerk AI-development, AI-automatisering, AI-agents, AI-kennisbanken, AI-consultancy, AI-lezingen en praktische AI-workshops voor bedrijven.",
    en: "Our services: custom AI development, AI automation, AI agents, AI knowledge systems, AI consultancy, AI lectures and practical AI workshops for businesses.",
  },
  h1: { nl: "Diensten", en: "Services" },
  parentId: SERVICE_INDEX_PARENT,
  indexable: true,
  lastModified: LAST_MODIFIED,
}

const servicePages: RegistryEntry[] = services.map((service) => ({
  id: `service:${service.id}` as PageId,
  pageType: "service" as const,
  path: {
    nl: localizedPath("nl", `diensten/${service.slug.nl}`),
    en: localizedPath("en", `services/${service.slug.en}`),
  },
  title: service.title,
  description: service.description,
  h1: service.h1,
  parentId: "services",
  indexable: true,
  lastModified: LAST_MODIFIED,
}))

// ---------------------------------------------------------------------------
// Location pages
// ---------------------------------------------------------------------------

const locationPages: RegistryEntry[] = locations.map((location) => ({
  id: `location:${location.id}` as PageId,
  pageType: "location" as const,
  path: {
    nl: localizedPath("nl", location.slug.nl!),
    en: localizedPath("en", location.slug.en!),
  },
  title: location.title,
  description: location.description,
  h1: location.h1,
  parentId: undefined,
  indexable: true,
  lastModified: LAST_MODIFIED,
}))

// ---------------------------------------------------------------------------
// Case studies
// ---------------------------------------------------------------------------

const caseIndex: RegistryEntry = {
  id: "cases",
  pageType: "case-index",
  path: { nl: "/nl/cases/", en: "/en/case-studies/" },
  title: {
    nl: "Cases | AI-projecten uit de praktijk",
    en: "Case Studies | AI Projects in Practice",
  },
  description: {
    nl: "Bekijk onze cases: contentautomatisering voor De Limburger, een RAG-navigatieassistent voor Burgerkracht, technische kennis voor monteurs bij Den Engelsen en een AI-tradingplatform voor een institutionele klant.",
    en: "Explore our case studies: content automation for De Limburger, a RAG navigation assistant for Burgerkracht, technical knowledge for Den Engelsen mechanics and an AI trading platform for an institutional client.",
  },
  h1: { nl: "Cases", en: "Case Studies" },
  parentId: SERVICE_INDEX_PARENT,
  indexable: true,
  lastModified: LAST_MODIFIED,
}

const casePages: RegistryEntry[] = cases.map((c) => ({
  id: `case:${c.id}` as PageId,
  pageType: "case-study" as const,
  path: {
    nl: localizedPath("nl", `cases/${c.slug.nl}`),
    en: localizedPath("en", `case-studies/${c.slug.en}`),
  },
  title: c.title,
  description: c.description,
  h1: c.h1,
  parentId: "cases",
  indexable: true,
  lastModified: c.slug ? LAST_MODIFIED : undefined,
}))

// ---------------------------------------------------------------------------
// Knowledge articles
// ---------------------------------------------------------------------------

const knowledgeIndex: RegistryEntry = {
  id: "insights",
  pageType: "knowledge-index",
  path: { nl: "/nl/kennis/", en: "/en/insights/" },
  title: {
    nl: "Kennis | Artikelen over AI-agents, RAG & Maatwerk AI",
    en: "Insights | Articles on AI Agents, RAG & Custom AI",
  },
  description: {
    nl: "Technische artikelen over AI-agents laten bouwen, RAG-chatbots, AI-kennisbanken en de kosten van maatwerk AI — geschreven vanuit onze eigen projectpraktijk.",
    en: "Technical articles on building AI agents, RAG chatbots, AI knowledge systems and the cost of custom AI — written from our own project practice.",
  },
  h1: { nl: "Kennis", en: "Insights" },
  parentId: SERVICE_INDEX_PARENT,
  indexable: true,
  lastModified: LAST_MODIFIED,
}

const knowledgePages: RegistryEntry[] = knowledgeArticles.map((article): RegistryEntry => {
  const path: Partial<Record<Locale, string>> = {}
  if (article.slug.nl) path.nl = localizedPath("nl", `kennis/${article.slug.nl}`)
  if (article.slug.en) path.en = localizedPath("en", `insights/${article.slug.en}`)
  const availableLocales = LOCALES.filter((l) => Boolean(article.slug[l]))
  return {
    id: article.id as PageId,
    pageType: "knowledge-article",
    path,
    // Article meta titles/descriptions exist only for published locales.
    title: Object.fromEntries(availableLocales.map((l) => [l, article.title[l]])) as Record<Locale, string>,
    description: Object.fromEntries(availableLocales.map((l) => [l, article.description[l]])) as Record<
      Locale,
      string
    >,
    h1: article.h1,
    parentId: "insights",
    indexable: true,
    lastModified: article.modified || article.published,
  }
})

// ---------------------------------------------------------------------------
// Full registry
// ---------------------------------------------------------------------------

export const registry: RegistryEntry[] = [
  ...staticPages,
  serviceIndex,
  ...servicePages,
  ...locationPages,
  caseIndex,
  ...casePages,
  knowledgeIndex,
  ...knowledgePages,
]

const byPath = new Map<string, RegistryEntry>()
for (const entry of registry) {
  for (const locale of LOCALES) {
    const p = entry.path[locale]
    if (p && !byPath.has(p)) byPath.set(p, entry)
  }
}

const byId = new Map<string, RegistryEntry>(registry.map((e) => [e.id, e]))

export function getPage(id: PageId): RegistryEntry | undefined {
  return byId.get(id)
}

/** String-keyed lookup for dynamically composed ids (e.g. `location:${city}`). */
export function getEntryByIdString(id: string): RegistryEntry | undefined {
  return byId.get(id)
}

export function getPathByIdString(id: string, locale: Locale): string | null {
  return byId.get(id)?.path[locale] ?? null
}

export function getPagePath(id: PageId, locale: Locale): string | null {
  return byId.get(id)?.path[locale] ?? null
}

export function getPageByPath(path: string): RegistryEntry | undefined {
  const normalized = path.length > 1 && !path.endsWith("/") ? `${path}/` : path
  return byPath.get(normalized)
}

/** All indexable paths across locales — used for prerendering and sitemap generation. */
export function getAllIndexablePaths(): string[] {
  return registry
    .filter((entry) => entry.indexable)
    .flatMap((entry) => Object.values(entry.path))
    .filter((p): p is string => Boolean(p))
    .sort()
}

export interface SitemapUrl {
  loc: string
  lastmod?: string
}

export function getSitemapUrls(): SitemapUrl[] {
  const urls: SitemapUrl[] = []
  for (const entry of registry) {
    if (!entry.indexable) continue
    for (const locale of LOCALES) {
      const p = entry.path[locale]
      if (p) urls.push({ loc: `${SITE_URL}${p}`, lastmod: entry.lastModified })
    }
  }
  return urls.sort((a, b) => a.loc.localeCompare(b.loc))
}

/** Reciprocal locale alternates for one page. */
export function getAlternates(entry: RegistryEntry): Partial<Record<Locale, string>> {
  return entry.path
}

/**
 * Legacy SPA routes → new locale routes.
 * Implemented server-side in nginx.conf; kept here so validators and docs can
 * verify them.
 */
export const LEGACY_REDIRECTS: { from: string; to: string }[] = [
  { from: "/", to: "/nl/" },
  { from: "/index.html", to: "/nl/" },
  { from: "/about", to: "/nl/over-ons/" },
  { from: "/about/", to: "/nl/over-ons/" },
  { from: "/portfolio", to: "/nl/cases/" },
  { from: "/portfolio/", to: "/nl/cases/" },
  { from: "/services", to: "/nl/diensten/" },
  { from: "/services/", to: "/nl/diensten/" },
  { from: "/contact", to: "/nl/contact/" },
  { from: "/contact/", to: "/nl/contact/" },
  { from: "/privacy", to: "/nl/privacy/" },
  { from: "/privacy/", to: "/nl/privacy/" },
]
